import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography } from '@mui/material';
import { ClientsHeader } from '@/clients/list/components/ClientsHeader';
import { ClientsFilterBar } from '@/clients/list/components/ClientsFilterBar';
import { ClientsContent } from '@/clients/list/components/ClientsContent';
import { ClientStatusTabs } from '@/clients/list/components/ClientStatusTabs';
import { ClientSidePanel } from '@/clients/list/components/ClientSidePanel';
import ScheduleAppointmentDialog from '@/components/appointments/ScheduleAppointmentDialog';
import { useClients } from '@/hooks/use-clients';
import { ClientDashboardItem, CLIENT_ROSTER_STATUS_FILTERS, type ClientRosterStatusFilter } from '@/types/client';
import { deleteClientDraft } from '@/lib/apiService';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';
import { DeleteClientConfirmDialog } from '@/components/clients/DeleteClientConfirmDialog';
import { useDeleteClient } from '@/hooks/useDeleteClient';

type ScheduleClientTarget = {
  id: string;
  name: string;
  email: string;
};

const VALID_STATUSES = CLIENT_ROSTER_STATUS_FILTERS;
type ClientStatus = ClientRosterStatusFilter;
type ClientsHookInitialStatus = ClientRosterStatusFilter;
type ClientsViewMode = 'grid' | 'list';

const escapeCsvValue = (value: unknown): string => {
  const normalized = String(value ?? '').trim();
  const escaped = normalized.replace(/"/g, '""');
  return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
};

const toCsvRow = (values: unknown[]): string => values.map((value) => escapeCsvValue(value)).join(',');

const formatDateForExport = (value: string | null | undefined): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const formatPhoneForExport = (client: unknown): string => {
  const source = client as Record<string, unknown>;
  const rawPhone = String(source.phone ?? '').trim();
  if (!rawPhone) return '';

  const phoneCountryCode = String(source.phoneCountryCode ?? '').trim();
  if (!phoneCountryCode) return rawPhone;

  const normalizedCountryCode = phoneCountryCode.startsWith('+')
    ? phoneCountryCode
    : `+${phoneCountryCode}`;

  if (rawPhone.startsWith('+')) return rawPhone;
  return `${normalizedCountryCode} ${rawPhone}`;
};

function parseStatus(raw: string | null): ClientStatus {
  return VALID_STATUSES.includes(raw as ClientStatus) ? (raw as ClientStatus) : 'all';
}

function parseViewMode(raw: string | null): ClientsViewMode {
  return raw === 'list' ? 'list' : 'grid';
}

function parsePositivePageParam(raw: string | null, fallback: number): number {
  return Math.max(1, parseInt(raw ?? String(fallback), 10) || fallback);
}

const ClientsListPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = useMemo(() => parseStatus(searchParams.get('status')), [searchParams]);
  const viewParam = useMemo(
    () => parseViewMode(searchParams.get('view') ?? searchParams.get('fromView')),
    [searchParams]
  );
  const [initialPage] = useState(() => parsePositivePageParam(searchParams.get('page'), 1));
  const [initialLimit] = useState(() => parsePositivePageParam(searchParams.get('limit'), 10));
  const initialHookStatus: ClientsHookInitialStatus = statusParam;

  const {
    filteredClients,
    clientStatus,
    searchQuery,
    locationFilter,
    locations,
    pagination,
    currentPage,
    statusTotals,
    setSearchQuery,
    setLocationFilter,
    setClientStatus,
    handleReactivateClient,
    handleSuspendClient,
    fetchClients,
    loading: clientsListLoading,
  } = useClients(initialHookStatus, { initialPage, initialLimit });

  const {
    deleteTarget,
    isDeletingClient,
    isDeleteDialogOpen,
    requestDeleteClient,
    closeDeleteClient,
    confirmDeleteClient,
  } = useDeleteClient();

  const [selectedPanelClientId, setSelectedPanelClientId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ClientsViewMode>(viewParam);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [scheduleClient, setScheduleClient] = useState<ScheduleClientTarget | null>(null);
  const [deletingDraftId, setDeletingDraftId] = useState<string | null>(null);

  const focusClientId = searchParams.get('focusClientId')?.trim() || null;

  const handleScheduleClient = useCallback((client: ClientDashboardItem) => {
    setScheduleClient({
      id: client.id,
      name: client.fullName?.trim() || 'Client',
      email: client.email?.trim() || '',
    });
    setIsScheduleDialogOpen(true);
  }, []);

  const handleCloseScheduleDialog = useCallback(() => {
    setIsScheduleDialogOpen(false);
    setScheduleClient(null);
  }, []);

  const handleClientCardClick = useCallback(
    (clientId: string) => {
      const page = searchParams.get('page') || '1';
      const limit = searchParams.get('limit') || '10';
      const status = parseStatus(searchParams.get('status'));
      let url = `/clients/${clientId}/details-v2/overview?fromPage=${page}&fromLimit=${limit}`;
      if (status !== 'all') url += `&fromStatus=${status}`;
      if (viewMode === 'list') url += '&fromView=list';
      navigate(url);
    },
    [navigate, searchParams, viewMode]
  );

  const handleDraftCardClick = useCallback(
    (draftId: string) => {
      const isDemoMode = searchParams.get('demo') === 'true';
      const returnTo = `/clients?status=draft&page=${currentPage}&limit=${pagination.limit}${
        isDemoMode ? '&demo=true' : ''
      }`;
      const query = new URLSearchParams();
      query.set('draftClientId', draftId);
      if (isDemoMode) query.set('demo', 'true');
      navigate(`/clients/add?${query.toString()}`, {
        state: {
          draftClientId: draftId,
          returnTo,
        },
      });
    },
    [currentPage, navigate, pagination.limit, searchParams]
  );

  const handleDeleteDraft = useCallback(
    async (draftId: string) => {
      if (deletingDraftId) return;

      setDeletingDraftId(draftId);
      try {
        const response = await deleteClientDraft(draftId);
        showSuccessToast('Draft deleted', response.message || 'Draft deleted permanently');
        const nextPage = filteredClients.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
        await fetchClients(nextPage, pagination.limit);
      } catch (error) {
        showErrorToast(error, 'Unable to delete draft');
      } finally {
        setDeletingDraftId(null);
      }
    },
    [currentPage, deletingDraftId, fetchClients, filteredClients.length, pagination.limit]
  );

  const handleViewModeChange = useCallback(
    (nextViewMode: ClientsViewMode) => {
      setViewMode(nextViewMode);
      const next = new URLSearchParams(searchParams);
      if (nextViewMode === 'list') {
        next.set('view', 'list');
      } else {
        next.delete('view');
        next.delete('fromView');
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Sync URL when page or limit changes
  useEffect(() => {
    const urlPage = parseInt(searchParams.get('page') ?? '1', 10) || 1;
    const urlLimit = parseInt(searchParams.get('limit') ?? '10', 10) || 10;
    if (currentPage === urlPage && pagination.limit === urlLimit) return;
    const next = new URLSearchParams(searchParams);
    next.set('page', String(currentPage));
    next.set('limit', String(pagination.limit));
    setSearchParams(next, { replace: true });
  }, [currentPage, pagination.limit, searchParams, setSearchParams]);

  // Keep roster tab in sync when arriving from a notification or shared URL.
  useEffect(() => {
    if (statusParam !== clientStatus) {
      setClientStatus(statusParam);
    }
  }, [clientStatus, setClientStatus, statusParam]);

  useEffect(() => {
    if (!focusClientId || clientsListLoading) return;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(`client-card-${focusClientId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [clientsListLoading, focusClientId, filteredClients]);

  useEffect(() => {
    setViewMode((currentViewMode) => (currentViewMode === viewParam ? currentViewMode : viewParam));
  }, [viewParam]);

  const handleStatusChange = useCallback(
    (status: ClientStatus) => {
      setClientStatus(status);
      const next = new URLSearchParams(searchParams);
      next.set('status', status);
      next.set('page', '1');
      if (!next.get('limit')) next.set('limit', String(pagination.limit));
      if (status !== 'pending_deletion') next.delete('focusClientId');
      setSearchParams(next, { replace: true });
    },
    [pagination.limit, searchParams, setClientStatus, setSearchParams]
  );

  const handleDeleteClient = useCallback((client: ClientDashboardItem) => {
    requestDeleteClient({
      id: client.id,
      name: client.fullName,
      status: client.status,
    });
  }, [requestDeleteClient]);

  const handleConfirmDeleteClient = useCallback(async () => {
    const deleted = await confirmDeleteClient();
    if (deleted) {
      await fetchClients(currentPage, pagination.limit);
    }
  }, [confirmDeleteClient, currentPage, fetchClients, pagination.limit]);

  const handleSearch = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value),
    [setSearchQuery]
  );

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, [setSearchQuery]);

  const handlePageChange = useCallback(
    (page: number) => fetchClients(page, pagination.limit),
    [fetchClients, pagination.limit]
  );

  const handleAddClientClick = useCallback(() => {
    const isDemoMode = searchParams.get('demo') === 'true';
    const returnTo = `/clients?status=${clientStatus}&page=${currentPage}&limit=${pagination.limit}${
      viewMode === 'list' ? '&view=list' : ''
    }${isDemoMode ? '&demo=true' : ''}`;
    navigate(`/clients/add${isDemoMode ? '?demo=true' : ''}`, {
      state: {
        returnTo,
      },
    });
  }, [clientStatus, currentPage, navigate, pagination.limit, searchParams, viewMode]);

  const handleExport = useCallback(() => {
    if (filteredClients.length === 0) {
      showWarningToast('No clients to export');
      return;
    }

    const headerRow = [
      'Client Name',
      'Email',
      'Phone',
      'Location',
      'Status',
      'Questionnaires Sent',
      'Questionnaire Progress (%)',
      'Upcoming Session Date',
      'Last Session Date',
    ];

    const dataRows = filteredClients.map((client) =>
      toCsvRow([
        client.fullName || '',
        client.email || '',
        formatPhoneForExport(client),
        client.location || '',
        client.status || '',
        client.questionnairesSentCount ?? 0,
        client.questionnaireProgress ?? '',
        formatDateForExport(client.upcomingSessionDate),
        formatDateForExport(client.lastSessionDate),
      ])
    );

    const csv = [toCsvRow(headerRow), ...dataRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const exportUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeStatus = String(clientStatus || 'clients').toLowerCase();
    const datePart = new Date().toISOString().slice(0, 10);

    link.href = exportUrl;
    link.download = `my-clients-${safeStatus}-${datePart}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(exportUrl);

    showSuccessToast(
      'Export started',
      `${filteredClients.length} client${filteredClients.length === 1 ? '' : 's'} exported as CSV.`
    );
  }, [clientStatus, filteredClients]);
  
  return (
    <Box
      data-demo-id="clients-page"
      sx={{ position: 'relative', py: { xs: 0.5, md: 1 }, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}
    >
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '30px',
          border: '1px solid #dbe5eb',
          bgcolor: '#f6f8fa',
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3, lg: 4 },
            py: { xs: 2, sm: 2.5, lg: 3 },
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 2.5, lg: 3 },
          }}
        >
          <Box
            sx={{
              borderRadius: '12px',
              border: '1px solid #e2e8ee',
              bgcolor: '#f7fafc',
              px: { xs: 1.5, sm: 2.25 },
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              overflowX: 'auto',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: '0.95rem',
                color: '#1f2937',
                whiteSpace: 'nowrap',
              }}
            >
              My Clients
            </Typography>
          </Box>

          <ClientsHeader onAddClient={handleAddClientClick} />

          <Box
            data-demo-id="clients-controls"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: { xs: 'stretch', md: 'center' },
              justifyContent: 'space-between',
              gap: { xs: 1.75, md: 2.5 },
              pb: 0,
              borderBottom: '1px solid #e5e9ee',
            }}
          >
            <ClientStatusTabs
              clientStatus={clientStatus}
              onStatusChange={handleStatusChange}
              statusTotals={statusTotals}
            />
            <ClientsFilterBar
              searchQuery={searchQuery}
              onSearchChange={handleSearch}
              onSearchClear={handleClearSearch}
              locations={locations}
              selectedLocation={locationFilter}
              onLocationSelect={setLocationFilter}
              onExport={handleExport}
              showFilterButton={false}
              viewMode={viewMode}
              onViewModeChange={handleViewModeChange}
            />
          </Box>

          <Box data-demo-id="clients-list">
            <ClientsContent
              clients={filteredClients}
              pagination={pagination}
              currentPage={currentPage}
              listLoading={clientsListLoading}
              hasAnyClients={statusTotals.all > 0 || statusTotals.pendingDeletion > 0 || statusTotals.drafts > 0}
              clientStatus={clientStatus}
              hasActiveFilters={searchQuery.trim() !== '' || locationFilter !== ''}
              statusTotals={statusTotals}
              onAddClient={handleAddClientClick}
              onReactivateClient={(clientId) => handleReactivateClient(clientId)}
              onSuspendClient={(clientId) => handleSuspendClient(clientId)}
              onDeleteClient={handleDeleteClient}
              currentStatus={clientStatus === 'all' ? 'active' : clientStatus}
              onPageChange={handlePageChange}
              onCardClick={handleClientCardClick}
              onOpenPanel={setSelectedPanelClientId}
              onScheduleClient={handleScheduleClient}
              onDraftClick={handleDraftCardClick}
              onDeleteDraft={handleDeleteDraft}
              deletingDraftId={deletingDraftId}
              viewMode={viewMode}
              highlightedClientId={focusClientId}
            />
          </Box>
        </Box>
      </Paper>
      <ClientSidePanel clientId={selectedPanelClientId} onClose={() => setSelectedPanelClientId(null)} />
      <ScheduleAppointmentDialog
        open={isScheduleDialogOpen}
        onClose={handleCloseScheduleDialog}
        defaultClientId={scheduleClient?.id ?? ''}
        lockedClient={
          scheduleClient
            ? {
                id: scheduleClient.id,
                name: scheduleClient.name,
                email: scheduleClient.email,
              }
            : undefined
        }
        onAppointmentCreated={() => {
          void fetchClients(currentPage, pagination.limit);
        }}
      />
      <DeleteClientConfirmDialog
        open={isDeleteDialogOpen}
        clientName={deleteTarget?.name}
        isPendingDeletion={deleteTarget?.isPendingDeletion}
        isDeleting={isDeletingClient}
        onOpenChange={(open) => {
          if (!open) closeDeleteClient();
        }}
        onConfirm={() => {
          void handleConfirmDeleteClient();
        }}
      />
    </Box>
  );
};

export default ClientsListPage;
